import React, { useState, useCallback } from 'react';
import './ProductTable.css';

const ProductTable = ({ 
  readOnly = false,
  products, 
  loading, 
  error, 
  updateProduct, 
  handleSort, 
  getSortIndicator, 
  isLowStock, 
  formatPrice,
  tableLocked,
  updating
}) => {

  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [originalValue, setOriginalValue] = useState(null);

  const handleCellClick = useCallback((productId, field, currentValue) => {
    if (readOnly || field === 'productId' || tableLocked) return;
    
    setEditingCell(`${productId}-${field}`);
    setEditValue(String(currentValue));
    setOriginalValue(currentValue);
  }, [readOnly, tableLocked]);

  const handleCellChange = useCallback((e) => {
    setEditValue(e.target.value);
  }, []);

  const handleCellSave = useCallback((productId, field) => {
    if (tableLocked) return;
    
    let value = editValue;
    
    // Type conversion based on field
    if (field === 'price' || field === 'kgPerMeter' || field === 'kgPerUnit' || field === 'thickness') {
      value = parseFloat(value) || 0;
    } else if (field === 'availability' || field === 'length' || field === 'width') {
      value = parseInt(value) || 0;
    }
    
    updateProduct(productId, field, value);
    setEditingCell(null);
    setEditValue('');
    setOriginalValue(null);
  }, [editValue, updateProduct, tableLocked]);

  const handleCellCancel = useCallback((productId, field) => {
    // Revert the product data to original value
    if (productId && field && originalValue !== null) {
      updateProduct(productId, field, originalValue);
    }
    setEditingCell(null);
    setEditValue('');
    setOriginalValue(null);
  }, [originalValue, updateProduct]);

  const handleKeyPress = useCallback((e, productId, field) => {
    if (e.key === 'Enter') {
      handleCellSave(productId, field);
    } else if (e.key === 'Escape') {
      handleCellCancel(productId, field);
    }
  }, [handleCellSave, handleCellCancel]);

  const renderCell = useCallback((product, field) => {
    const cellKey = `${product.productId}-${field}`;
    const isEditing = editingCell === cellKey;
    const value = product[field];

    if (isEditing) {
      return (
        <input
          type={field === 'price' || field === 'kgPerMeter' || field === 'kgPerUnit' || field === 'thickness' || field === 'availability' || field === 'length' || field === 'width' ? 'number' : 'text'}
          value={editValue}
          onChange={handleCellChange}
          onKeyPress={(e) => handleKeyPress(e, product.productId, field)}
          onBlur={() => handleCellCancel(product.productId, field)}
          className="edit-input"
          autoFocus
        />
      );
    }

    let displayValue = value;
    if (field === 'price') {
      displayValue = formatPrice(value);
    }

    return (
      <span 
        className={`cell-content ${field === 'availability' && isLowStock(value) ? 'low-stock' : ''}`}
        onClick={() => handleCellClick(product.productId, field, value)}
        title={readOnly ? 'Read only' : 'Click to edit'}
      >
        {displayValue}
      </span>
    );
  }, [editingCell, editValue, handleCellChange, handleCellSave, handleCellCancel, handleCellClick, handleKeyPress, formatPrice, isLowStock, readOnly]);

  if (loading) {
    return (
      <div className="table-container">
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Loading products...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="table-container">
        <div className="error-message">
          <p>Error loading products: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`table-container ${tableLocked ? 'table-locked' : ''}`}>
      {updating && (
        <div className="updating-overlay">
          <div className="spinner"></div>
          <p>Updating products...</p>
        </div>
      )}
      <div className="table-wrapper">
        <table className="product-table">
          <thead className="table-header">
            <tr>
              <th onClick={() => handleSort('productId')} className="sortable">
                Product ID {getSortIndicator('productId')}
              </th>
              <th onClick={() => handleSort('name')} className="sortable">
                Name {getSortIndicator('name')}
              </th>
              <th onClick={() => handleSort('material')} className="sortable">
                Material {getSortIndicator('material')}
              </th>
              <th onClick={() => handleSort('type')} className="sortable">
                Type {getSortIndicator('type')}
              </th>
              <th onClick={() => handleSort('thickness')} className="sortable">
                Thickness {getSortIndicator('thickness')}
              </th>
              <th onClick={() => handleSort('length')} className="sortable">
                Length {getSortIndicator('length')}
              </th>
              <th onClick={() => handleSort('width')} className="sortable">
                Width {getSortIndicator('width')}
              </th>
              <th onClick={() => handleSort('price')} className="sortable">
                Price {getSortIndicator('price')}
              </th>
              <th onClick={() => handleSort('availability')} className="sortable">
                Availability {getSortIndicator('availability')}
              </th>
            </tr>
          </thead>
          <tbody className="table-body">
            {products.map((product) => (
              <tr key={product.productId} className="table-row">
                <td className={`table-cell product-id-cell ${editingCell === `${product.productId}-productId` ? 'editing' : ''}`}>
                  {renderCell(product, 'productId')}
                </td>
                <td className={`table-cell name-cell ${editingCell === `${product.productId}-name` ? 'editing' : ''}`}>
                  {renderCell(product, 'name')}
                </td>
                <td className={`table-cell material-cell ${editingCell === `${product.productId}-material` ? 'editing' : ''}`}>
                  {renderCell(product, 'material')}
                </td>
                <td className={`table-cell type-cell ${editingCell === `${product.productId}-type` ? 'editing' : ''}`}>
                  {renderCell(product, 'type')}
                </td>
                <td className={`table-cell thickness-cell ${editingCell === `${product.productId}-thickness` ? 'editing' : ''}`}>
                  {renderCell(product, 'thickness')}
                </td>
                <td className={`table-cell length-cell ${editingCell === `${product.productId}-length` ? 'editing' : ''}`}>
                  {renderCell(product, 'length')} {product.lengthUnit}
                </td>
                <td className={`table-cell width-cell ${editingCell === `${product.productId}-width` ? 'editing' : ''}`}>
                  {renderCell(product, 'width')} {product.widthUnit}
                </td>
                <td className={`table-cell price-cell ${editingCell === `${product.productId}-price` ? 'editing' : ''}`}>
                  {renderCell(product, 'price')}
                </td>
                <td className={`table-cell availability-cell ${editingCell === `${product.productId}-availability` ? 'editing' : ''}`}>
                  {renderCell(product, 'availability')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductTable;
